<template>
  <NotificationGroup
    v-for="pos in positions"
    v-bind:key="pos"
    :class="`position__${pos}`"
  >
    <Reveal
      v-for="notification in getNotifications(pos)"
      :key="notification.id"
      :appear="true"
    >
      <NotificationComponent
        :type="{
          style: notification.type,
          icon: notification.showIcon == undefined || notification.showIcon,
        }"
        :closable="notification.closable == undefined || notification.closable"
        @close="onClose(notification)"
        data-cy="notification"
      >
        <span>{{ notification.message }}</span></NotificationComponent
      >
    </Reveal>
  </NotificationGroup>
</template>

<script setup lang="ts">
import { useNotificationStore } from "@/stores/useNotificationStore";
import type { Notification } from "@/Domain/Notification";
import { PositionEnum } from "@/Domain/PositionEnum";
import { ref } from "vue";
import { Reveal } from "@progress/kendo-vue-animation";
import {
  Notification as NotificationComponent,
  NotificationGroup,
} from "@progress/kendo-vue-notification";

const store = useNotificationStore();

const activeNotifications = ref<Notification[]>([]);

const positions = Object.values(PositionEnum);

const handleNotification = (notification: Notification) => {
  if (!notification.id)
    notification.id = Math.floor(Math.random() * Date.now()).toString();
  activeNotifications.value.push(notification);
  if (notification.duration) {
    setTimeout(() => {
      onClose(notification);
    }, notification.duration);
  }
};

const onClose = (notification: Notification) => {
  activeNotifications.value = activeNotifications.value.filter(
    (n) => n.id !== notification.id
  );
  notification.onClose && notification.onClose();
};

const getNotifications = function (position: PositionEnum) {
  return activeNotifications.value.filter((notification) =>
    notification.position
      ? notification.position === position
      : PositionEnum.TopRight === position
  );
};

store.handleNotification = handleNotification;
</script>

<style lang="scss">
.k-notification-group {
  z-index: 2000;

  .k-notification {
    margin-block: 0.5rem;
  }
}
.position {
  &__topLeft {
    top: 0;
    left: 0;
    margin-left: 1rem;
  }

  &__topCenter {
    top: 0;
    left: 50%;
    transform: translateX(-50%);
  }

  &__topRight {
    top: 0;
    right: 0;
    margin-right: 1rem;
  }

  &__bottomLeft {
    bottom: 0;
    left: 0;
    margin-left: 1rem;
  }

  &__bottomCenter {
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
  }

  &__bottomRight {
    bottom: 0;
    right: 0;
    margin-right: 1rem;
  }
}
</style>
